import React, { useState } from 'react';
import {
  Grid,
  Card,
  CardMedia,
  IconButton,
  Typography,
  Box,
  Paper,
  Button,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  MenuItem,
  Chip
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  CloudUpload as CloudUploadIcon,
  Star as StarIcon,
  MoreVert as MoreVertIcon
} from '@mui/icons-material';

const PhotoSection = ({ 
  userProfile, 
  isMobile, 
  onFileSelect, 
  onDeletePhoto,
  onSetMainPhoto 
}) => {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [photoToDelete, setPhotoToDelete] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(null);

  const photoCount = userProfile.photos?.length || 0;
  const maxPhotos = 10;

  const getStatusColor = () => {
    if (photoCount === 0) return '#ef4444';
    if (photoCount < 3) return '#f59e0b';
    return '#22c55e';
  };

  const getStatusText = () => {
    if (photoCount === 0) return 'Aucune photo - Ajoutez des photos pour attirer vos clients';
    if (photoCount < 3) return `${photoCount} photo${photoCount > 1 ? 's' : ''} - Ajoutez-en ${3 - photoCount} de plus pour optimiser votre visibilité`;
    return `${photoCount} photos - Parfait ! Votre salon est attractif`;
  };

  const handleDeleteClick = (photo, index) => {
    setPhotoToDelete({ photo, index });
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (photoToDelete) {
      onDeletePhoto(photoToDelete.photo, photoToDelete.index);
    }
    setDeleteDialogOpen(false);
    setPhotoToDelete(null);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setPhotoToDelete(null);
  };

  const handlePhotoMenuClick = (event, index) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedPhotoIndex(index);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedPhotoIndex(null);
  };

  const handleSetAsMain = () => {
    if (selectedPhotoIndex !== null && onSetMainPhoto) {
      onSetMainPhoto(selectedPhotoIndex);
    }
    handleMenuClose();
  };

  const handleDeleteFromMenu = () => {
    if (selectedPhotoIndex !== null) {
      const photo = userProfile.photos[selectedPhotoIndex];
      handleDeleteClick(photo, selectedPhotoIndex);
    }
    handleMenuClose();
  };

  return (
    <Box>
      {/* Status Bar */}
      <Box 
        sx={{ 
          mb: 3,
          p: 2,
          borderRadius: 2,
          background: 'linear-gradient(135deg, #f5f5dc 0%, #faf8f3 100%)',
          border: '1px solid',
          borderColor: 'grey.200'
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, color: 'grey.700' }}>
            Photos de votre salon
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: getStatusColor()
              }}
            />
            <Typography variant="caption" sx={{ color: 'grey.600' }}>
              {photoCount}/{maxPhotos}
            </Typography>
          </Box>
        </Box>
        <Typography variant="body2" sx={{ color: 'grey.600', fontSize: '0.875rem' }}>
          {getStatusText()}
        </Typography>
        {photoCount < maxPhotos && (
          <Button
            size="small"
            onClick={() => document.getElementById('photo-upload').click()}
            sx={{ 
              mt: 1,
              textTransform: 'none',
              fontWeight: 600
            }}
          >
            Ajouter des photos
          </Button>
        )}
      </Box>

      {/* Upload Zone when no photos */}
      {photoCount === 0 && (
        <Paper
          sx={{
            p: isMobile ? 4 : 6,
            textAlign: 'center',
            border: '2px dashed',
            borderColor: 'grey.300',
            bgcolor: 'grey.50',
            cursor: 'pointer',
            mb: 3,
            borderRadius: 3,
            '&:hover': {
              borderColor: 'primary.main',
              bgcolor: 'primary.50'
            },
            transition: 'all 0.2s ease-in-out'
          }}
          onClick={() => document.getElementById('photo-upload').click()}
        >
          <CloudUploadIcon 
            sx={{ 
              fontSize: isMobile ? 48 : 64, 
              color: 'grey.400', 
              mb: 2
            }} 
          />
          <Typography 
            variant={isMobile ? "h6" : "h5"} 
            sx={{ 
              mb: 1, 
              color: 'grey.700',
              fontWeight: 600
            }}
          >
            Ajoutez vos premières photos
          </Typography>
          <Typography 
            variant="body2" 
            color="text.secondary" 
            sx={{ mb: 3 }}
          >
            Cliquez ici pour sélectionner des photos de votre salon
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Formats acceptés: JPG, PNG • Taille max: 5MB par photo
          </Typography>
        </Paper>
      )}

      {/* Photos Grid */}
      <Grid container spacing={isMobile ? 2 : 3}>
        {userProfile.photos && userProfile.photos.map((photo, index) => (
          <Grid item xs={isMobile ? 6 : 12} sm={6} md={4} lg={3} key={index}>
            <Card 
              sx={{ 
                position: 'relative',
                borderRadius: 2,
                overflow: 'hidden',
                cursor: 'pointer',
                '&:hover .photo-actions': {
                  opacity: 1
                },
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                },
                transition: 'all 0.2s ease-in-out'
              }}
            >
              <CardMedia
                component="img"
                height={isMobile ? "140" : "180"}
                image={photo}
                alt={`Photo ${index + 1} du salon`}
                sx={{ objectFit: 'cover' }}
              />
              
              {/* Photo principale badge */}
              {index === 0 && (
                <Chip
                  onClick={() => {}}
                  icon={<StarIcon fontSize="small" />}
                  label="Photo principale"
                  size="small"
                  sx={{
                    position: 'absolute',
                    top: 8,
                    left: 8,
                    bgcolor: 'rgba(34, 197, 94, 0.95)',
                    color: 'white',
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    '& .MuiChip-icon': {
                      color: 'white'
                    }
                  }}
                />
              )}

              {/* Actions overlay */}
              <Box
                className="photo-actions"
                sx={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  left: 0,
                  bottom: 0,
                  bgcolor: 'rgba(0,0,0,0.3)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: 0,
                  transition: 'opacity 0.2s ease-in-out'
                }}
              >
                <IconButton
                  onClick={(e) => handlePhotoMenuClick(e, index)}
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.9)',
                    color: 'grey.700',
                    '&:hover': {
                      bgcolor: 'white'
                    }
                  }}
                >
                  <MoreVertIcon />
                </IconButton>
              </Box>
            </Card>
          </Grid>
        ))}
        
        {/* Add Photo Button */}
        {photoCount < maxPhotos && photoCount > 0 && (
          <Grid item xs={isMobile ? 6 : 12} sm={6} md={4} lg={3}>
            <Paper
              sx={{
                height: isMobile ? 140 : 180,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                border: '2px dashed',
                borderColor: 'grey.300',
                bgcolor: 'grey.50',
                borderRadius: 2,
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: 'primary.50'
                },
                transition: 'all 0.2s ease-in-out'
              }}
              onClick={() => document.getElementById('photo-upload').click()}
            >
              <AddIcon 
                sx={{ 
                  fontSize: 32, 
                  color: 'grey.400', 
                  mb: 1
                }} 
              />
              <Typography 
                variant="body2" 
                color="text.secondary"
                sx={{ fontWeight: 500 }}
              >
                Ajouter une photo
              </Typography>
              {!isMobile && (
                <Typography 
                  variant="caption" 
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  {maxPhotos - photoCount} restante{maxPhotos - photoCount > 1 ? 's' : ''}
                </Typography>
              )}
            </Paper>
          </Grid>
        )}
      </Grid>

      {/* Photo Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {selectedPhotoIndex !== 0 && (
          <MenuItem onClick={handleSetAsMain}>
            <StarIcon sx={{ mr: 1, fontSize: 18 }} />
            Définir comme photo principale
          </MenuItem>
        )}
        <MenuItem onClick={handleDeleteFromMenu} sx={{ color: 'error.main' }}>
          <DeleteIcon sx={{ mr: 1, fontSize: 18 }} />
          Supprimer cette photo
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          Supprimer cette photo ?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Êtes-vous sûr de vouloir supprimer cette photo ? Cette action est irréversible.
          </Typography>
          {photoToDelete?.index === 0 && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                bgcolor: 'warning.50',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'warning.200'
              }}
            >
              <Typography variant="body2" sx={{ color: 'warning.800', fontWeight: 500 }}>
                ⚠️ Attention : Cette photo est actuellement votre photo principale.
              </Typography>
              {userProfile.photos && userProfile.photos.length > 1 && (
                <Typography variant="body2" sx={{ color: 'warning.700', mt: 1 }}>
                  Si vous la supprimez, la photo suivante deviendra automatiquement votre nouvelle photo principale.
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button 
            onClick={handleDeleteCancel}
            variant="outlined"
            sx={{ textTransform: 'none' }}
          >
            Annuler
          </Button>
          <Button 
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            sx={{ textTransform: 'none' }}
          >
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Simple Tips */}
      <Box 
        sx={{ 
          mt: 4,
          p: 3,
          borderRadius: 2,
          background: 'linear-gradient(135deg, #f0ead6 0%, #faf8f3 100%)',
          border: '1px solid',
          borderColor: 'grey.300'
        }}
      >
        <Typography 
          variant="body2" 
          sx={{ 
            fontWeight: 600, 
            mb: 2,
            color: '#8b5a2b'
          }}
        >
          Conseils pour de belles photos
        </Typography>
        
        <Stack spacing={1}>
          <Typography variant="body2" sx={{ color: '#6b4423' }}>
            • Utilisez la lumière naturelle (près d'une fenêtre)
          </Typography>
          <Typography variant="body2" sx={{ color: '#6b4423' }}>
            • Montrez l'intérieur de votre salon et vos équipements
          </Typography>
          <Typography variant="body2" sx={{ color: '#6b4423' }}>
            • Ajoutez des photos de vos réalisations (avant/après)
          </Typography>
          <Typography variant="body2" sx={{ color: '#6b4423' }}>
            • Assurez-vous que les photos sont nettes et bien cadrées
          </Typography>
        </Stack>
      </Box>

      <input
        id="photo-upload"
        type="file"
        accept="image/*"
        multiple
        style={{ display: 'none' }}
        onChange={onFileSelect}
      />
    </Box>
  );
};

export default PhotoSection;
